import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineCube,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark,
  HiOutlineArrowPath,
  HiOutlineTag,
  HiOutlineCircleStack,
  HiOutlineClock,
  HiOutlineCurrencyDollar,
  HiOutlineCheckCircle,
  HiOutlineScale,
  HiOutlineInboxStack
} from 'react-icons/hi2';
import { authService, productService, productSkuService } from '../../services';
import { usePermissions } from '../../hooks/usePermissions';
import SharedModal from '../../components/common/SharedModal';
import PageLoading from '../../components/common/PageLoading';
import './Products.css';

const ProductSkus = () => {
  const [skus, setSkus] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [productFilter, setProductFilter] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [editingSku, setEditingSku] = useState(null);
  const [formData, setFormData] = useState({
    productId: '',
    skuCode: '',
    skuName: '',
    sizeValue: '',
    sizeUnit: '',
    uom: '',
    cycleTime: '',
    costPrice: '',
    status: 'Active',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();
  const { hasPermission } = usePermissions();

  // Reusing Product permissions for SKUs as they are closely related
  const canView = hasPermission('Product.View');
  const canCreate = hasPermission('Product.Create');
  const canEdit = hasPermission('Product.Edit');
  const canDelete = hasPermission('Product.Delete');

  useEffect(() => {
    if (tenantId && canView) {
      const timer = setTimeout(() => {
        loadData();
      }, 0);
      return () => clearTimeout(timer);
    } else if (!canView) {
      setError('You do not have permission to view product SKUs');
    } else {
      setError('Please login to view product SKUs');
    }
  }, [tenantId, canView]);

  const loadData = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError('');
      const [productsData, skusData] = await Promise.all([
        productService.getAllProducts(),
        productSkuService.getSkusByTenant()
      ]);
      setProducts(productsData || []);
      setSkus(skusData || []);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredSkus = skus.filter((sku) => {
    const matchesSearch =
      sku.skuName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sku.skuCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sku.product?.productName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus =
      statusFilter === 'All' ||
      (statusFilter === 'Active' && sku.status === 'Active') ||
      (statusFilter === 'Inactive' && sku.status === 'Inactive');

    const matchesProduct = 
      productFilter === 'All' ||
      sku.productId.toString() === productFilter;
    
    return matchesSearch && matchesStatus && matchesProduct;
  });

  const handleAdd = () => {
    if (!canCreate) {
      setError('You do not have permission to create SKUs');
      return;
    }
    setEditingSku(null);
    setFormData({
      productId: '',
      skuCode: '',
      skuName: '',
      sizeValue: '',
      sizeUnit: '',
      uom: '',
      cycleTime: '',
      costPrice: '',
      status: 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (sku) => {
    if (!canEdit) {
      setError('You do not have permission to edit SKUs');
      return;
    }
    setEditingSku(sku);
    setFormData({
      productId: sku.productId,
      skuCode: sku.skuCode,
      skuName: sku.skuName,
      sizeValue: sku.sizeValue,
      sizeUnit: sku.sizeUnit || '',
      uom: sku.uom || '',
      cycleTime: sku.cycleTime,
      costPrice: sku.costPrice || '',
      status: sku.status || 'Active',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (sku) => {
    try {
      setError('');
      // Hard delete if not used, otherwise handle error. 
      // Or maybe soft delete by setting status inactive if your backend supports it.
      // Based on Products.js, it seems we might want soft delete behavior or handle hard delete failure.
      // Controller has dependency check logic and returns 400 if used.
      
      const success = await productSkuService.deleteSku(sku.skuId);
      if (success) {
          await loadData(false);
          setDeleteConfirm(null);
      } else {
           setError('Failed to delete SKU.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete SKU. It might be in use.');
      console.error('Error deleting SKU:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    const codeRegex = /^[a-zA-Z0-9-_]+$/;
    const nameRegex = /^[A-Za-z0-9\s().,/-]+$/;

    // Required field checks
    if (!formData.productId) errors.productId = 'Product is required';
    
    if (!formData.skuCode.trim()) {
      errors.skuCode = 'SKU Code is required';
    } else if (formData.skuCode.length > 50) {
      errors.skuCode = 'SKU Code must be less than 50 characters';
    } else if (!codeRegex.test(formData.skuCode.trim())) {
      errors.skuCode = 'Code must be alphanumeric (a-z, 0-9, -, _)';
    } else {
      // Duplicate check
      const isDuplicate = skus.some(s => 
        s.skuCode.toLowerCase() === formData.skuCode.trim().toLowerCase() && 
        s.skuId !== editingSku?.skuId
      );
      if (isDuplicate) errors.skuCode = 'SKU Code already exists';
    }

    if (!formData.skuName.trim()) {
      errors.skuName = 'SKU Name is required';
    } else if (formData.skuName.length > 150) {
      errors.skuName = 'SKU Name must be less than 150 characters';
    } else if (!nameRegex.test(formData.skuName.trim())) {
      errors.skuName = 'Name contains invalid characters';
    }

    const sizeVal = parseFloat(formData.sizeValue);
    if (!formData.sizeValue) {
      errors.sizeValue = 'Size Value is required';
    } else if (isNaN(sizeVal) || sizeVal <= 0) {
      errors.sizeValue = 'Size must be greater than 0';
    }

    if (formData.sizeUnit && formData.sizeUnit.length > 20) {
      errors.sizeUnit = 'Size Unit too long (max 20)';
    }

    if (formData.uom && formData.uom.length > 20) {
      errors.uom = 'UOM too long (max 20)';
    }

    const cycleVal = parseFloat(formData.cycleTime);
    if (!formData.cycleTime) {
      errors.cycleTime = 'Cycle Time is required';
    } else if (isNaN(cycleVal) || cycleVal <= 0) {
      errors.cycleTime = 'Cycle Time must be greater than 0';
    }

    if (formData.costPrice) {
      const costVal = parseFloat(formData.costPrice);
      if (isNaN(costVal) || costVal <= 0) {
        errors.costPrice = 'Cost must be greater than 0';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (editingSku && !canEdit) {
      setError('You do not have permission to edit SKUs');
      return;
    }
    if (!editingSku && !canCreate) {
      setError('You do not have permission to create SKUs');
      return;
    }
    
    if (!validateForm()) return;

    try {
      setSubmitting(true);
      setError('');

      const skuData = {
        productId: parseInt(formData.productId),
        skuCode: formData.skuCode.trim(),
        skuName: formData.skuName.trim(),
        sizeValue: parseFloat(formData.sizeValue),
        sizeUnit: formData.sizeUnit.trim() || '',
        uom: formData.uom.trim() || '',
        cycleTime: parseFloat(formData.cycleTime),
        costPrice: formData.costPrice ? parseFloat(formData.costPrice) : null,
        status: formData.status
      };

      if (editingSku) {
        skuData.skuId = editingSku.skuId;
        await productSkuService.updateSku(editingSku.skuId, skuData);
      } else {
        await productSkuService.createSku(skuData);
      }

      await loadData(false);
      setShowModal(false);
      setEditingSku(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.message || 
                          'Failed to save SKU. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSku(null);
    setFormErrors({});
  };

  if (loading && skus.length === 0) {
    return <PageLoading message="Loading product SKUs..." />;
  }

  return (
    <div className="products-page">
      <div className="page-header">
        <div className="header-content">
          <h2>SKU Master</h2>
          <p>Manage product variants (SKUs)</p>
        </div>
        {canCreate && (
          <button className="btn-primary" onClick={handleAdd}>
            <HiOutlinePlus className="btn-icon" />
            Add SKU
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search SKUs..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="filter-group">
            <select
                value={productFilter}
                onChange={(e) => setProductFilter(e.target.value)}
                className="status-filter"
                style={{ maxWidth: '200px' }}
            >
                <option value="All">All Products</option>
                {products.map(p => (
                    <option key={p.productId} value={p.productId}>{p.productName}</option>
                ))}
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="status-filter"
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="table-info">
            {loading ? (
              <PageLoading variant="muted" message="Loading…" />
            ) : (
              `${filteredSkus.length} SKUs`
            )}
          </div>
        </div>

        {loading ? (
          <div className="table-loading">
            <PageLoading variant="compact" message="Loading SKUs…" />
          </div>
        ) : filteredSkus.length === 0 ? (
          <div className="empty-state">
            <HiOutlineCube className="empty-icon" />
            <h3>No SKUs found</h3>
            <p>
              {searchTerm || productFilter !== 'All'
                ? 'Try adjusting your filters'
                : 'Get started by creating your first SKU'}
            </p>
            {!searchTerm && productFilter === 'All' && canCreate && (
              <button className="btn-primary" onClick={handleAdd}>
                New SKU
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>SKU Name</th>
                <th>Product</th>
                <th>Size</th>
                <th>UOM</th>
                <th>Cycle Time</th>
                <th>Cost / Price</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSkus.map((sku) => (
                <tr key={sku.skuId} className={sku.status === 'Inactive' ? 'inactive-row' : ''}>
                  <td>{sku.skuCode}</td>
                  <td className="name-cell">{sku.skuName}</td>
                  <td>{sku.product?.productName}</td>
                  <td>{sku.sizeValue} {sku.sizeUnit}</td>
                  <td>{sku.uom || '-'}</td>
                  <td>{sku.cycleTime}</td>
                  <td>{sku.costPrice !== null && sku.costPrice !== undefined ? sku.costPrice : '-'}</td>
                  <td>
                    <span className={`status-badge ${sku.status?.toLowerCase()}`}>
                      {sku.status || 'Active'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {canEdit && (
                        <button
                          className="btn-icon-edit"
                          onClick={() => handleEdit(sku)}
                          title="Edit"
                        >
                          <HiOutlinePencil />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className="btn-icon-delete"
                          onClick={() => setDeleteConfirm(sku)}
                          title="Delete"
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingSku ? 'Edit SKU' : 'New SKU'}
        customWidth="800px"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="sku-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="loading-spinner-sm"></div>
                  Saving...
                </>
              ) : (
                <>
                  {editingSku ? <HiOutlinePencil /> : <HiOutlinePlus />}
                  {editingSku ? 'Update SKU' : 'Create SKU'}
                </>
              )}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="sku-form" onSubmit={handleSubmit} className="modal-form">
            <div className="sku-modal-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Product Select - Full Width */}
              <div className="form-group" style={{ gridColumn: 'span 2' }}>
                <label htmlFor="productId" className="premium-label">
                  <HiOutlineCube className="label-icon" /> Product <span className="required">*</span>
                </label>
                <div className="input-with-icon">
                  <select
                    id="productId"
                    value={formData.productId}
                    onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                    className={formErrors.productId ? 'error' : ''}
                    disabled={!!editingSku}
                  >
                    <option value="">Select Product</option>
                    {products.map(p => (
                      <option key={p.productId} value={p.productId}>{p.productName}</option>
                    ))}
                  </select>
                </div>
                {formErrors.productId && <span className="field-error">{formErrors.productId}</span>}
              </div>

              {/* SKU Code */}
              <div className="form-group">
                <label htmlFor="skuCode" className="premium-label">
                  <HiOutlineTag className="label-icon" /> SKU Code <span className="required">*</span>
                </label>
                <div className="input-with-icon">
                  <input
                    type="text"
                    id="skuCode"
                    value={formData.skuCode}
                    onChange={(e) => setFormData({ ...formData, skuCode: e.target.value })}
                    className={formErrors.skuCode ? 'error' : ''}
                    placeholder="e.g. SKU-001"
                    maxLength={50}
                  />
                </div>
                {formErrors.skuCode && <span className="field-error">{formErrors.skuCode}</span>}
              </div>

              {/* SKU Name */}
              <div className="form-group">
                <label htmlFor="skuName" className="premium-label">
                  <HiOutlineInboxStack className="label-icon" /> SKU Name <span className="required">*</span>
                </label>
                <div className="input-with-icon">
                  <input
                    type="text"
                    id="skuName"
                    value={formData.skuName}
                    onChange={(e) => setFormData({ ...formData, skuName: e.target.value })}
                    className={formErrors.skuName ? 'error' : ''}
                    placeholder="e.g. Premium Blend"
                    maxLength={150}
                  />
                </div>
                {formErrors.skuName && <span className="field-error">{formErrors.skuName}</span>}
              </div>

              {/* Size Value */}
              <div className="form-group">
                <label htmlFor="sizeValue" className="premium-label">
                  <HiOutlineScale className="label-icon" /> Size Value <span className="required">*</span>
                </label>
                <div className="input-with-icon">
                  <input
                    type="number"
                    step="0.01"
                    id="sizeValue"
                    value={formData.sizeValue}
                    onChange={(e) => setFormData({ ...formData, sizeValue: e.target.value })}
                    className={formErrors.sizeValue ? 'error' : ''}
                    placeholder="0.00"
                  />
                </div>
                {formErrors.sizeValue && <span className="field-error">{formErrors.sizeValue}</span>}
              </div>

              {/* Size Unit */}
              <div className="form-group">
                <label htmlFor="sizeUnit" className="premium-label">
                  <HiOutlineScale className="label-icon" /> Size Unit
                </label>
                <div className="input-with-icon">
                  <input
                    type="text"
                    id="sizeUnit"
                    value={formData.sizeUnit}
                    onChange={(e) => setFormData({ ...formData, sizeUnit: e.target.value })}
                    placeholder="e.g. ml, g, kg"
                    maxLength={20}
                    className={formErrors.sizeUnit ? 'error' : ''}
                  />
                </div>
                {formErrors.sizeUnit && <span className="field-error">{formErrors.sizeUnit}</span>}
              </div>

              {/* UOM */}
              <div className="form-group">
                <label htmlFor="uom" className="premium-label">
                  <HiOutlineCircleStack className="label-icon" /> UOM
                </label>
                <div className="input-with-icon">
                  <input
                    type="text"
                    id="uom"
                    value={formData.uom}
                    onChange={(e) => setFormData({ ...formData, uom: e.target.value })}
                    placeholder="e.g. Box, Case, Pcs"
                    maxLength={20}
                    className={formErrors.uom ? 'error' : ''}
                  />
                </div>
                {formErrors.uom && <span className="field-error">{formErrors.uom}</span>}
              </div>

              {/* Cycle Time */}
              <div className="form-group">
                <label htmlFor="cycleTime" className="premium-label">
                  <HiOutlineClock className="label-icon" /> Cycle Time (sec) <span className="required">*</span>
                </label>
                <div className="input-with-icon">
                  <input
                    type="number"
                    step="0.001"
                    id="cycleTime"
                    value={formData.cycleTime}
                    onChange={(e) => setFormData({ ...formData, cycleTime: e.target.value })}
                    className={formErrors.cycleTime ? 'error' : ''}
                    placeholder="0.000"
                  />
                </div>
                {formErrors.cycleTime && <span className="field-error">{formErrors.cycleTime}</span>}
              </div>

              {/* Cost/Price */}
              <div className="form-group">
                <label htmlFor="costPrice" className="premium-label">
                  <HiOutlineCurrencyDollar className="label-icon" /> Cost / Price
                </label>
                <div className="input-with-icon">
                  <input
                    type="number"
                    step="0.01"
                    id="costPrice"
                    value={formData.costPrice}
                    onChange={(e) => setFormData({ ...formData, costPrice: e.target.value })}
                    placeholder="0.00"
                    className={formErrors.costPrice ? 'error' : ''}
                  />
                </div>
                {formErrors.costPrice && <span className="field-error">{formErrors.costPrice}</span>}
              </div>

              {/* Status */}
              <div className="form-group">
                <label htmlFor="status" className="premium-label">
                  <HiOutlineCheckCircle className="label-icon" /> Status
                </label>
                <div className="input-with-icon">
                  <select
                    id="status"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete SKU <strong>{deleteConfirm?.skuCode}</strong>?
          </p>
          <p className="warning-text">
            This action ensures the SKU is removed only if not used in OEE calculations.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default ProductSkus;
